import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Trash2, Box } from "lucide-react";

export default function ProjectCard({ design, onRequestDelete }) {
  const isReviewed = design.status === "sent_for_review";
  const thumb = design.thumbnail_data_url;
  const placeholderThumbs = [
    "https://images.unsplash.com/photo-1627141234469-24711efb373c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    "https://images.unsplash.com/photo-1771756743534-4fe2cda63f0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    "https://images.unsplash.com/photo-1718893389568-22a2a039998c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  ];
  const fallback =
    placeholderThumbs[
      Math.abs(
        (design.design_id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0),
      ) % placeholderThumbs.length
    ];

  return (
    <Link
      to={`/designs/${design.design_id}`}
      className="group relative block border border-zinc-800 bg-zinc-950 lift-hover"
      data-testid="project-card-link"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-zinc-900">
        {thumb ? (
          <img
            src={thumb}
            alt={design.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <>
            <img
              src={fallback}
              alt={design.name}
              className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
            />
            <div className="absolute inset-0 bp-grid-fine pointer-events-none" />
          </>
        )}
        <div className="absolute top-2 left-2 font-mono text-[9px] uppercase tracking-widest text-brass-400 bg-zinc-950/80 px-2 py-1 border border-zinc-800">
          {design.style || "Modern"}
        </div>
        {isReviewed && (
          <div className="absolute top-2 right-2 font-mono text-[9px] uppercase tracking-widest text-orange-400 bg-zinc-950/80 px-2 py-1 border border-orange-700/60">
            Sent for Review
          </div>
        )}
        {onRequestDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRequestDelete(design);
            }}
            data-testid="project-card-delete-btn"
            aria-label={`Delete ${design.name}`}
            className={
              /* V2.0.5 — Always-visible 44 × 44 tap target on mobile so
                 phone users can delete without hover. Tablet+ keeps the
                 fade-in-on-hover behaviour for a cleaner desktop grid. */
              "absolute bottom-2 right-2 inline-grid place-items-center w-11 h-11 sm:w-8 sm:h-8 border bg-zinc-950/80 backdrop-blur " +
              "border-zinc-700 text-zinc-300 hover:border-orange-500 hover:text-orange-300 active:border-orange-500 active:text-orange-300 hover:bg-zinc-950 transition-colors " +
              "opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
            }
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-display text-base text-zinc-50 leading-tight tracking-tight truncate">
            {design.name}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1.5">
            {design.square_footage?.toLocaleString?.() ?? design.square_footage} sqft •{" "}
            {design.bedrooms} bd • {design.bathrooms} ba
          </div>
          {/* V2.0.15 — BIM-Ready trust badge. Surfaces the IFC + PDF export
              capability at a glance on every design card; doubles as a soft
              upsell for free-tier users browsing their own boards. */}
          <div
            data-testid="project-card-bim-badge"
            className="mt-2 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-brass-300 border border-brass-500/40 bg-brass-500/5 px-2 py-0.5"
            title="Export this design as IFC (Revit / ArchiCAD / Vectorworks) or as a clean architectural PDF."
          >
            <Box className="w-2.5 h-2.5" />
            // BIM-Ready · IFC + PDF
          </div>
        </div>
        <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-brass-400 transition-colors flex-shrink-0 mt-1" />
      </div>
    </Link>
  );
}
